import {
	ArrowLeftOutlined,
	ArrowRightOutlined,
	MergeCellsOutlined,
	ReloadOutlined,
} from '@ant-design/icons'
import {
	Alert,
	Button,
	Card,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	Typography,
	message,
} from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { staffService } from '../../common/service/staff.service'
import { useThemeColors } from '../../common/ThemeContext'

const { Title, Text, Paragraph } = Typography

type Cluster = {
	staffIds: string[]
	reasons: string[]
	members: Array<{
		id: string
		email?: string
		phoneNumber?: string
		firstName?: string
		lastName?: string
		nameInEnglish?: string
	}>
}

const COMPARE_KEYS: { key: string; label: string }[] = [
	{ key: 'id', label: 'Staff ID' },
	{ key: 'firstName', label: 'First name' },
	{ key: 'lastName', label: 'Last name' },
	{ key: 'nameInEnglish', label: 'Name (English)' },
	{ key: 'email', label: 'Email' },
	{ key: 'phoneNumber', label: 'Phone' },
	{ key: 'bankAccountNumber', label: 'Bank account' },
	{ key: 'departmentId', label: 'Department ID' },
	{ key: 'roleId', label: 'Role ID' },
	{ key: 'shiftId', label: 'Shift / team ID' },
	{ key: 'sex', label: 'Sex' },
	{ key: 'status', label: 'Status' },
	{ key: 'ranking', label: 'Ranking' },
	{ key: 'extraShift', label: 'Extra shift' },
	{ key: 'isExtraShift', label: 'Extra shift flag' },
	{ key: 'bio', label: 'Bio' },
	{ key: 'experience', label: 'Experience' },
	{ key: 'education', label: 'Education' },
	{ key: 'firstLineManager', label: 'First line manager' },
	{ key: 'secondLineManager', label: 'Second line manager' },
	{ key: 'thirdLineManager', label: 'Third line manager' },
]

function flattenStaffDetail(s: any): Record<string, any> {
	if (!s) return {}
	return {
		...s,
		departmentId: s.department?.id ?? s.departmentId,
		roleId: s.role?.id ?? s.roleId,
		shiftId: s.shift?.id ?? s.shiftId,
	}
}

function formatCell(key: string, v: any): string {
	if (v === null || v === undefined || v === '') return '—'
	if (key === 'firstLineManager' || key === 'secondLineManager' || key === 'thirdLineManager') {
		const id = v?.id
		return id ? String(id) : '—'
	}
	if (typeof v === 'boolean') return v ? 'Yes' : 'No'
	if (typeof v === 'object') return JSON.stringify(v)
	return String(v)
}

function buildMergePayload(merged: Record<string, any>): Record<string, any> {
	const allow = new Set(COMPARE_KEYS.map((c) => c.key))
	const out: Record<string, any> = {}
	for (const k of allow) {
		if (k in merged && merged[k] !== undefined) {
			out[k] = merged[k]
		}
	}
	delete out.id
	return out
}

export default function StaffMerge() {
	const { primary } = useThemeColors()
	const [loading, setLoading] = useState(false)
	const [clusters, setClusters] = useState<Cluster[]>([])
	const [modalOpen, setModalOpen] = useState(false)
	const [activeCluster, setActiveCluster] = useState<Cluster | null>(null)
	const [keepId, setKeepId] = useState<string | undefined>()
	const [removeId, setRemoveId] = useState<string | undefined>()
	const [keepDetail, setKeepDetail] = useState<any>(null)
	const [removeDetail, setRemoveDetail] = useState<any>(null)
	const [merged, setMerged] = useState<Record<string, any>>({})
	const [detailLoading, setDetailLoading] = useState(false)
	const [merging, setMerging] = useState(false)
	const didInitialScanRef = useRef(false)

	const loadScan = useCallback(async () => {
		setLoading(true)
		try {
			const res: any = await staffService.duplicateScan()
			if (res?.statusCode === 200 && res?.data?.clusters) {
				setClusters(res.data.clusters)
				if (!res.data.clusters.length) {
					message.open({
						key: 'staff-merge-scan-empty',
						type: 'info',
						content: 'No duplicate email or phone groups found.',
					})
				}
			}
		} catch (e) {
			console.error(e)
			message.error('Could not scan for duplicates.')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		// Prevent duplicate initial scan/toast in React StrictMode (dev double-invoke).
		if (didInitialScanRef.current) return
		didInitialScanRef.current = true
		loadScan()
	}, [loadScan])

	const openResolve = (c: Cluster) => {
		setActiveCluster(c)
		const ids = c.staffIds
		setKeepId(ids[0])
		setRemoveId(ids.length > 1 ? ids[1] : undefined)
		setKeepDetail(null)
		setRemoveDetail(null)
		setMerged({})
		setModalOpen(true)
	}

	useEffect(() => {
		if (!modalOpen || !keepId || !removeId || keepId === removeId) {
			return
		}
		let cancelled = false
		;(async () => {
			setDetailLoading(true)
			try {
				const [ka, kb] = await Promise.all([
					staffService.detail(keepId),
					staffService.detail(removeId),
				])
				if (cancelled) return
				const left = flattenStaffDetail(ka?.data)
				const right = flattenStaffDetail(kb?.data)
				setKeepDetail(left)
				setRemoveDetail(right)
				setMerged({ ...left })
			} catch (e) {
				console.error(e)
				message.error('Failed to load staff details.')
			} finally {
				if (!cancelled) setDetailLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [modalOpen, keepId, removeId])

	const memberOptions = useMemo(() => {
		if (!activeCluster) return []
		return activeCluster.members.map((m) => ({
			label: `${m.id} — ${m.nameInEnglish || `${m.firstName || ''} ${m.lastName || ''}`.trim() || '—'}`,
			value: m.id,
		}))
	}, [activeCluster])

	const keepOptions = useMemo(
		() => memberOptions.filter((o) => o.value !== removeId),
		[memberOptions, removeId],
	)

	const removeOptions = useMemo(
		() => memberOptions.filter((o) => o.value !== keepId),
		[memberOptions, keepId],
	)

	const compareRows = useMemo(() => {
		return COMPARE_KEYS.map(({ key, label }) => {
			const keepVal = keepDetail?.[key]
			const removeVal = removeDetail?.[key]
			const mergedVal = merged[key]
			const diverges =
				formatCell(key, keepVal) !== formatCell(key, removeVal) &&
				keepDetail &&
				removeDetail
			return {
				key,
				label,
				keepVal,
				removeVal,
				mergedVal,
				diverges,
			}
		})
	}, [keepDetail, removeDetail, merged])

	const closeModal = () => {
		setModalOpen(false)
		setActiveCluster(null)
	}

	const submitMerge = async () => {
		if (!keepId || !removeId || keepId === removeId) {
			message.warning('Choose two different staff records.')
			return
		}
		const mergedFields = buildMergePayload({ ...merged, id: keepId })
		setMerging(true)
		try {
			const res: any = await staffService.mergeStaff({
				keepStaffId: keepId,
				removeStaffId: removeId,
				mergedFields:
					Object.keys(mergedFields).length > 0 ? mergedFields : undefined,
			})
			if (res?.statusCode === 200) {
				message.success(res?.data?.message || 'Staff merged successfully.')
				closeModal()
				await loadScan()
			}
		} catch (e: any) {
			console.error(e)
			const msg =
				e?.response?.data?.message?.[0] ||
				e?.response?.data?.message ||
				e?.message ||
				'Merge failed.'
			message.error(typeof msg === 'string' ? msg : 'Merge failed.')
		} finally {
			setMerging(false)
		}
	}

	return (
		<>
			<GlobalHeader />
			<div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
				<div
					style={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						gap: 16,
						marginBottom: 24,
						flexWrap: 'wrap',
					}}
				>
					<div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
						<div
							style={{
								width: 48,
								height: 48,
								borderRadius: 10,
								background: primary,
								color: '#fff',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
							}}
						>
							<MergeCellsOutlined style={{ fontSize: 22 }} />
						</div>
						<div>
							<Title
								level={3}
								style={{ margin: 0, color: primary, fontFamily: 'Times New Roman' }}
							>
								Merge duplicate staff
							</Title>
							<Paragraph type='secondary' style={{ margin: '4px 0 0' }}>
								Finds groups with the same email or phone. Resolve conflicts so sign-in maps
								to a single staff record.
							</Paragraph>
						</div>
					</div>
					<Button icon={<ReloadOutlined />} onClick={loadScan} loading={loading}>
						Scan again
					</Button>
				</div>

				<Alert
					type='info'
					showIcon
					style={{ marginBottom: 20, borderRadius: 8 }}
					message='Why this matters'
					description='Duplicate email or phone rows can make staff sign-in attach to the wrong record. After merging, run a new scan to confirm the group is gone.'
				/>

				<Card
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: 0 } }}
				>
					<Table<Cluster>
						rowKey={(r) => r.staffIds.join(',')}
						loading={loading}
						dataSource={clusters}
						pagination={false}
						columns={[
							{
								title: 'Match',
								key: 'reasons',
								width: 220,
								render: (_, row) => (
									<Space wrap size={[4, 4]}>
										{row.reasons.map((t) => (
											<Tag key={t} color='volcano'>
												{t}
											</Tag>
										))}
									</Space>
								),
							},
							{
								title: 'Staff IDs',
								key: 'ids',
								render: (_, row) => (
									<Text code>{row.staffIds.join(', ')}</Text>
								),
							},
							{
								title: 'People',
								key: 'names',
								render: (_, row) => (
									<ul style={{ margin: 0, paddingLeft: 18 }}>
										{row.members.map((m) => (
											<li key={m.id}>
												<Text strong>{m.id}</Text>{' '}
												{m.nameInEnglish ||
													`${m.firstName || ''} ${m.lastName || ''}`.trim() ||
													'—'}
												{m.email ? (
													<Text type='secondary'>{` · ${m.email}`}</Text>
												) : null}
											</li>
										))}
									</ul>
								),
							},
							{
								title: '',
								key: 'act',
								width: 120,
								render: (_, row) => (
									<Button type='primary' size='small' onClick={() => openResolve(row)}>
										Resolve
									</Button>
								),
							},
						]}
					/>
				</Card>
			</div>

			<Modal
				title={
					<Space>
						<MergeCellsOutlined />
						<span>Merge duplicate staff</span>
					</Space>
				}
				open={modalOpen}
				onCancel={closeModal}
				width={960}
				footer={null}
				destroyOnClose
			>
				<Space direction='vertical' size={16} style={{ width: '100%' }}>
					<Paragraph type='secondary' style={{ marginBottom: 0 }}>
						Choose which record to <Text strong>keep</Text> and which to{' '}
						<Text strong>remove</Text>. Then pick field values (left = keep record, right =
						remove record).
					</Paragraph>
					<Space wrap style={{ width: '100%' }}>
						<Space direction='vertical' size={4}>
							<Text type='secondary'>Keep (survivor)</Text>
							<Select
								style={{ minWidth: 280 }}
								options={keepOptions}
								value={keepId}
								onChange={(v) => setKeepId(v)}
							/>
						</Space>
						<Space direction='vertical' size={4}>
							<Text type='secondary'>Remove</Text>
							<Select
								style={{ minWidth: 280 }}
								options={removeOptions}
								value={removeId}
								onChange={(v) => setRemoveId(v)}
							/>
						</Space>
					</Space>

					<Table
						size='small'
						loading={detailLoading}
						pagination={false}
						rowKey='key'
						dataSource={compareRows}
						columns={[
							{
								title: 'Field',
								dataIndex: 'label',
								width: 160,
								render: (t, row) => (
									<Text strong={row.diverges}>{t}</Text>
								),
							},
							{
								title: 'Keep record',
								dataIndex: 'keepVal',
								render: (_, row) => formatCell(row.key, row.keepVal),
							},
							{
								title: '',
								key: 'l',
								width: 72,
								render: (_, row) => (
									<Button
										type='text'
										size='small'
										icon={<ArrowLeftOutlined />}
										onClick={() =>
											setMerged((m) => ({
												...m,
												[row.key]: keepDetail?.[row.key],
											}))
										}
										disabled={!keepDetail}
									/>
								),
							},
							{
								title: '',
								key: 'r',
								width: 72,
								render: (_, row) => (
									<Button
										type='text'
										size='small'
										icon={<ArrowRightOutlined />}
										onClick={() =>
											setMerged((m) => ({
												...m,
												[row.key]: removeDetail?.[row.key],
											}))
										}
										disabled={!removeDetail}
									/>
								),
							},
							{
								title: 'Remove record',
								dataIndex: 'removeVal',
								render: (_, row) => formatCell(row.key, row.removeVal),
							},
							{
								title: 'Result',
								dataIndex: 'mergedVal',
								render: (_, row) => (
									<Text code>{formatCell(row.key, row.mergedVal)}</Text>
								),
							},
						]}
					/>

					<Space style={{ justifyContent: 'flex-end', width: '100%' }}>
						<Button onClick={closeModal}>Cancel</Button>
						<Button type='primary' loading={merging} onClick={submitMerge}>
							Merge and remove duplicate
						</Button>
					</Space>
				</Space>
			</Modal>
		</>
	)
}
